import React, { useState } from 'react';
import Card from '../components/ProfileCard.tsx';
import Badge from '../components/Badge';
import SearchInput from '../components/SearchInput';
import Pagination from '../components/Pagination';
import { Filter, Star, StarHalf } from 'lucide-react';

const UserFeedback: React.FC = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [filterRating, setFilterRating] = useState<number | 'All'>('All');

    // Sample data
    const feedbacks = [
        {
            id: 1,
            userName: 'John Smith',
            rating: 5,
            comments: 'The template is amazing! It has all the features I needed and was very easy to customize.',
            termsAccepted: true,
            createdAt: '2025-03-01',
            templateName: 'E-commerce Pro',
        },
        {
            id: 2,
            userName: 'Sarah Johnson',
            rating: 4,
            comments: 'Great template overall. The documentation could be a bit more detailed, but the design is excellent.',
            termsAccepted: true,
            createdAt: '2025-03-05',
            templateName: 'Portfolio Plus',
        },
        {
            id: 3,
            userName: 'Michael Brown',
            rating: 5,
            comments: 'Absolutely love this template! The support team was also very helpful when I had questions.',
            termsAccepted: true,
            createdAt: '2025-03-08',
            templateName: 'Blog Master',
        },
        {
            id: 4,
            userName: 'Emily Davis',
            rating: 2,
            comments: 'The template looks good but I had many issues with mobile responsiveness. Had to fix several things myself.',
            termsAccepted: true,
            createdAt: '2025-03-10',
            templateName: 'Landing Page Pro',
        },
        {
            id: 5,
            userName: 'David Wilson',
            rating: 3,
            comments: 'Decent template for the price. Some features were missing that were shown in the preview.',
            termsAccepted: true,
            createdAt: '2025-03-15',
            templateName: 'Admin Dashboard',
        },
        {
            id: 6,
            userName: 'Jennifer Lee',
            rating: 5,
            comments: 'This template saved me so much time! The code is clean and well-organized.',
            termsAccepted: true,
            createdAt: '2025-03-18',
            templateName: 'E-commerce Pro',
        },
        {
            id: 7,
            userName: 'Robert Taylor',
            rating: 4,
            comments: 'Very good design and functionality. I would recommend this to others.',
            termsAccepted: true,
            createdAt: '2025-03-20',
            templateName: 'Portfolio Plus',
        },
        {
            id: 8,
            userName: 'Lisa Anderson',
            rating: 1,
            comments: 'I had many issues with this template and the support was not helpful at all.',
            termsAccepted: true,
            createdAt: '2025-03-22',
            templateName: 'Blog Master',
        },
        {
            id: 9,
            userName: 'James Martin',
            rating: 5,
            comments: 'Excellent template! It has exceeded my expectations in every way.',
            termsAccepted: true,
            createdAt: '2025-03-25',
            templateName: 'Landing Page Pro',
        },
        {
            id: 10,
            userName: 'Patricia White',
            rating: 4,
            comments: 'Very good template with lots of customization options.',
            termsAccepted: true,
            createdAt: '2025-03-28',
            templateName: 'Admin Dashboard',
        },
    ];

    // Filter and search
    const filteredFeedbacks = feedbacks.filter((feedback) => {
        const matchesSearch =
            feedback.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            feedback.comments.toLowerCase().includes(searchTerm.toLowerCase()) ||
            feedback.templateName.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesRating = filterRating === 'All' || feedback.rating === filterRating;

        return matchesSearch && matchesRating;
    });

    // Pagination
    const itemsPerPage = 5;
    const totalPages = Math.ceil(filteredFeedbacks.length / itemsPerPage);
    const paginatedFeedbacks = filteredFeedbacks.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    // Calculate average rating
    const averageRating = feedbacks.reduce((sum, feedback) => sum + feedback.rating, 0) / feedbacks.length;

    // Rating distribution
    const ratingDistribution = [1, 2, 3, 4, 5].map(rating => ({
        rating,
        count: feedbacks.filter(f => f.rating === rating).length,
        percentage: (feedbacks.filter(f => f.rating === rating).length / feedbacks.length) * 100,
    }));

    // Render stars
    const renderStars = (rating: number) => {
        const stars = [];
        const fullStars = Math.floor(rating);
        const hasHalfStar = rating % 1 !== 0;

        for (let i = 0; i < fullStars; i++) {
            stars.push(<Star key={`full-${i}`} size={16} className="text-yellow-400 fill-current" />);
        }

        if (hasHalfStar) {
            stars.push(<StarHalf key="half" size={16} className="text-yellow-400 fill-current" />);
        }

        const emptyStars = 5 - stars.length;
        for (let i = 0; i < emptyStars; i++) {
            stars.push(<Star key={`empty-${i}`} size={16} className="text-gray-300" />);
        }

        return stars;
    };

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-gray-900">User Feedback</h1>
                <p className="mt-1 text-sm text-gray-500">
                    View and analyze customer feedback and ratings
                </p>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <Card>
                    <div className="flex flex-col items-center">
                        <h3 className="text-lg font-medium text-gray-900">Average Rating</h3>
                        <div className="mt-2 flex items-center">
              <span className="text-5xl font-bold text-gray-900 mr-2">
                {averageRating.toFixed(1)}
              </span>
                            <div className="flex">
                                {renderStars(averageRating)}
                            </div>
                        </div>
                        <p className="mt-2 text-sm text-gray-500">
                            Based on {feedbacks.length} reviews
                        </p>
                    </div>
                </Card>

                <Card>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Rating Distribution</h3>
                    <div className="space-y-3">
                        {ratingDistribution.map((item) => (
                            <div key={item.rating} className="flex items-center">
                                <div className="w-12 text-sm font-medium text-gray-900">
                                    {item.rating} {item.rating === 1 ? 'star' : 'stars'}
                                </div>
                                <div className="flex-1 mx-2">
                                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-yellow-400"
                                            style={{ width: `${item.percentage}%` }}
                                        ></div>
                                    </div>
                                </div>
                                <div className="w-12 text-sm font-medium text-gray-500 text-right">
                                    {item.count}
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <SearchInput
                    placeholder="Search feedback..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="md:w-64"
                />

                <div className="flex items-center space-x-2">
                    <Filter size={20} className="text-gray-400" />
                    <select
                        value={filterRating === 'All' ? 'All' : filterRating.toString()}
                        onChange={(e) => setFilterRating(e.target.value === 'All' ? 'All' : parseInt(e.target.value))}
                        className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                    >
                        <option value="All">All Ratings</option>
                        <option value="5">5 Stars</option>
                        <option value="4">4 Stars</option>
                        <option value="3">3 Stars</option>
                        <option value="2">2 Stars</option>
                        <option value="1">1 Star</option>
                    </select>
                </div>
            </div>

            <div className="space-y-4">
                {paginatedFeedbacks.length > 0 ? (
                    paginatedFeedbacks.map((feedback) => (
                        <Card key={feedback.id} className="hover:shadow-md transition-shadow">
                            <div className="flex flex-col md:flex-row md:items-start gap-4">
                                <div className="flex-1">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-lg font-medium text-gray-900">{feedback.userName}</h3>
                                        <div className="flex">
                                            {renderStars(feedback.rating)}
                                        </div>
                                    </div>
                                    <p className="mt-1 text-sm text-gray-500">
                                        Template: {feedback.templateName}
                                    </p>
                                    <p className="mt-2 text-sm text-gray-700">{feedback.comments}</p>
                                    <div className="mt-2 flex items-center text-xs text-gray-500">
                                        <span>{feedback.createdAt}</span>
                                        <span className="mx-2">•</span>
                                        <span>
                      {feedback.termsAccepted ? (
                          <Badge variant="success">Terms Accepted</Badge>
                      ) : (
                          <Badge variant="danger">Terms Not Accepted</Badge>
                      )}
                    </span>
                                    </div>
                                </div>
                            </div>
                        </Card>
                    ))
                ) : (
                    <Card>
                        <div className="text-center py-6">
                            <p className="text-gray-500">No feedback found.</p>
                        </div>
                    </Card>
                )}
            </div>

            {filteredFeedbacks.length > itemsPerPage && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                />
            )}
        </div>
    );
};

export default UserFeedback;